import Typography from '@mui/material/Typography';
import { useSortedGoals } from 'features/goals';
import { useSortedLocalizedLifeAreas } from 'features/life-areas';
import { useCreateSmartListAllowed } from 'features/smart-lists';
import { useSortedTags } from 'features/tags';
import {
  useAllTasksFilterDocument,
  useUpdateAllTasksFilterDocument,
} from 'features/user';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { TopBarBase } from 'shared/components/connected/top-bar-base';
import { FilterButton } from 'shared/components/ui/filter-button';
import { MobileTodoNavigation } from 'shared/components/ui/mobile-todo-navigation';
import { FilterDocuments } from 'shared/constants';
import { useOpenPremiumDialog } from 'shared/contexts/premium-dialog';
import { SmartListFormDialog } from 'shared/dialogs/smart-list-form-dialog';
import { useActivePath } from 'shared/hooks/use-active-path';
import { useOpenMenu } from 'shared/hooks/use-open-menu';
import { Paths } from 'shared/routes';
import { GroupByMode, TaskGroupBy } from 'shared/types/sorting-mode';

import { ExtraOptionsMenu } from './extra-options-menu';
import * as Styled from './page-top-bar.style';

const supportedGroupByModes: TaskGroupBy[] = [
  GroupByMode.None,
  GroupByMode.Goal,
  GroupByMode.LifeArea,
  GroupByMode.Priority,
  GroupByMode.Type,
  GroupByMode.Deadline,
  GroupByMode.DeadlineDeadline,
  GroupByMode.Tag,
];

export const PageTopBar: React.FC = () => {
  const { t } = useTranslation();
  const activePath = useActivePath({ level: 2 });
  const { allowed: createSmartListAllowed } = useCreateSmartListAllowed();
  const openPremiumDialog = useOpenPremiumDialog();
  const {
    menuOpen: createSmartListOpen,
    openMenu: openCreateSmartList,
    closeMenu: closeCreateSmartList,
  } = useOpenMenu();

  const onCreateSmartList = useCallback(() => {
    if (!createSmartListAllowed) {
      openPremiumDialog();
      return;
    }
    openCreateSmartList();
  }, [createSmartListAllowed, openCreateSmartList, openPremiumDialog]);

  const filterDocument = useAllTasksFilterDocument();
  const updateFilterDocument = useUpdateAllTasksFilterDocument();

  const goals = useSortedGoals();
  const tags = useSortedTags();
  const lifeAreas = useSortedLocalizedLifeAreas({ activeOnly: true });

  const onReset = () =>
    updateFilterDocument(FilterDocuments.AllTasks.defaultFilterDocument);

  return (
    <TopBarBase
      mobileNavigationSlot={
        <MobileTodoNavigation activePath={activePath as Paths} />
      }
    >
      <Styled.Container>
        <Styled.Title>
          <Typography variant="h5" component="h1">
            {t('pages.all-tasks.title')}
          </Typography>
        </Styled.Title>

        <Styled.Interactions>
          <FilterButton
            filterDocument={filterDocument}
            defaultFilterDocument={
              FilterDocuments.AllTasks.defaultFilterDocument
            }
            goals={goals}
            lifeAreas={lifeAreas}
            tags={tags}
            updateFilterDocument={updateFilterDocument}
            supportedGroupByModes={supportedGroupByModes}
            onReset={onReset}
            onCreateSmartList={onCreateSmartList}
            preventDrag
            renderAsIconButton
          />
          <ExtraOptionsMenu
            filterDocument={filterDocument}
            updateFilterDocument={updateFilterDocument}
          />
        </Styled.Interactions>
      </Styled.Container>
      {createSmartListOpen && (
        <SmartListFormDialog
          open
          onClose={closeCreateSmartList}
          filterDocument={filterDocument}
        />
      )}
    </TopBarBase>
  );
};
